<template>
  <ReportChartCard :title="chartTitle" full-height is-relative>
    <VueApexCharts
      class="bg-white"
      type="line"
      :options="mergedChartOptions"
      :series="series"
      height="400"
      @click="handleChartClick"
      @updated="clearMarkers"
      @zoomed="clearMarkers"
      @panned="clearMarkers" />
    <ChartMarker v-for="(marker, index) in fixedMarkers" :key="index" :marker="marker" @remove="fixedMarkers.splice(index, 1)" />
  </ReportChartCard>
</template>

<script setup lang="ts">
import ChartMarker from '@/components/Globals/Charts/ChartMarker.vue'
import { useI18n } from '@/composable/useI18n'
import { convertToLocalTimeUnix } from '@/composable/useFormatDateAndHours'
import type { ILineChartMarker } from '@/interfaces/components/chats/chart-marker'
import { useReportStore } from '@/store/report'
import { useCompareTemperatureChart, useTemperatureChart } from '@/utils/components/temperature-chart'
import {
  mergeCorrelatedChartOptions,
  type CorrelatedChartPair,
} from '@/utils/report/correlated-charts-layout'
import { isMobileWindow } from '@/utils/screen/window'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import VueApexCharts from 'vue3-apexcharts'
import ReportChartCard from './ReportChartCard.vue'

const props = defineProps<{
  pair: CorrelatedChartPair
  showXAxisLabels: boolean
}>()

const { t } = useI18n()
const { basePeriod, comparePeriod } = storeToRefs(useReportStore())

const isBasePair = computed(() => props.pair === 'base')
const baseChart = useTemperatureChart()
const compareChart = useCompareTemperatureChart()

const chartOptions = computed(() =>
  isBasePair.value ? baseChart.chartOptions.value : compareChart.chartOptions.value,
)
const series = computed(() => (isBasePair.value ? baseChart.series.value : compareChart.series.value))

const period = computed(() => (isBasePair.value ? basePeriod.value : comparePeriod.value))

const timeRange = computed(() => {
  const [startDate, endDate] = period.value || []
  return {
    min: startDate ? convertToLocalTimeUnix(startDate) : undefined,
    max: endDate ? convertToLocalTimeUnix(endDate) : undefined,
  }
})

const mergedChartOptions = computed(() =>
  mergeCorrelatedChartOptions((chartOptions.value ?? {}) as Record<string, unknown>, {
    showXAxisLabels: props.showXAxisLabels,
    xAxisMin: timeRange.value.min,
    xAxisMax: timeRange.value.max,
  }),
)

const chartTitle = computed(() =>
  isBasePair.value ? t('report.temperature.title') : t('report.temperature.title_compare'),
)

const fixedMarkers = ref<ILineChartMarker[]>([])

function toMarkerDate(value: Date | string | number): Date {
  return value instanceof Date ? value : new Date(value)
}

function clearMarkers() {
  fixedMarkers.value = []
}

const handleChartClick = (
  event: MouseEvent,
  _chartContext: unknown,
  config: { dataPointIndex?: number; seriesIndex?: number },
) => {
  if (isMobileWindow()) return

  const dataPointIndex = config.dataPointIndex ?? -1
  const seriesIndex = config.seriesIndex ?? -1
  if (dataPointIndex >= 0 && seriesIndex >= 0) {
    const seriesName = series.value[seriesIndex].name
    const dataPoint = series.value[seriesIndex].data[dataPointIndex]
    const chartRect = (event.target as HTMLElement).getBoundingClientRect()

    fixedMarkers.value.push({
      seriesName,
      x: toMarkerDate(dataPoint.x),
      y: `${parseFloat(String(dataPoint.y)).toFixed(2)} C`,
      positionX: event.clientX - chartRect.left,
      positionY: event.clientY - chartRect.top,
    })
  }
}
</script>
